import { Settings, User } from 'lucide-react'
import { useState, useEffect } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTeamStageOwners, useUpdateTeamStageOwners } from '@/hooks'
import { useTeamMembers, type DeveloperGroup } from '@/hooks'
import { cn } from '@/lib/utils'

interface TeamStageOwnersProps {
  teamId: string
  isArchived?: boolean
}

/**
 * 阶段负责人配置
 */
const STAGE_OWNER_CONFIG = [
  {
    key: 'uiDesignOwnerId' as const,
    label: 'UI设计负责人',
    description: '负责 UI设计 和 UI确认 阶段',
    allowedGroups: ['design'] as DeveloperGroup[],
  },
  {
    key: 'frontendOwnerId' as const,
    label: '前端开发负责人',
    description: '负责 前端开发 阶段',
    allowedGroups: ['frontend'] as DeveloperGroup[],
  },
  {
    key: 'backendOwnerId' as const,
    label: '后端开发负责人',
    description: '负责 后端开发 阶段',
    allowedGroups: ['backend'] as DeveloperGroup[],
  },
  {
    key: 'testingOwnerId' as const,
    label: '测试负责人',
    description: '负责 测试 阶段',
    allowedGroups: ['testing'] as DeveloperGroup[],
  },
]

/**
 * 团队阶段负责人配置组件
 * Requirements: 2.9
 */
export function TeamStageOwners({ teamId, isArchived }: TeamStageOwnersProps) {
  const { data: stageOwners, isLoading: ownersLoading } = useTeamStageOwners(teamId)
  const { data: members, isLoading: membersLoading } = useTeamMembers(teamId)
  const updateMutation = useUpdateTeamStageOwners()

  const [formData, setFormData] = useState<{
    uiDesignOwnerId: string | null
    frontendOwnerId: string | null
    backendOwnerId: string | null
    testingOwnerId: string | null
  }>({
    uiDesignOwnerId: null,
    frontendOwnerId: null,
    backendOwnerId: null,
    testingOwnerId: null,
  })

  const [hasChanges, setHasChanges] = useState(false)

  // 用于存储原始数据的引用
  const [originalData, setOriginalData] = useState<{
    uiDesignOwnerId: string | null
    frontendOwnerId: string | null
    backendOwnerId: string | null
    testingOwnerId: string | null
  } | null>(null)

  // 初始化表单数据
  useEffect(() => {
    if (stageOwners) {
      const data = {
        uiDesignOwnerId: stageOwners.uiDesignOwnerId || null,
        frontendOwnerId: stageOwners.frontendOwnerId || null,
        backendOwnerId: stageOwners.backendOwnerId || null,
        testingOwnerId: stageOwners.testingOwnerId || null,
      }
      setFormData(data)
      setOriginalData(data)
    } else if (stageOwners === null) {
      // API 返回 null，表示没有配置
      const data = {
        uiDesignOwnerId: null,
        frontendOwnerId: null,
        backendOwnerId: null,
        testingOwnerId: null,
      }
      setOriginalData(data)
    }
  }, [stageOwners])

  // 检查是否有变更
  useEffect(() => {
    if (!originalData) {
      setHasChanges(false)
      return
    }
    // 规范化比较：将 undefined 和空字符串都视为 null
    const normalize = (v: string | null | undefined) => v || null
    const changed =
      normalize(formData.uiDesignOwnerId) !== normalize(originalData.uiDesignOwnerId) ||
      normalize(formData.frontendOwnerId) !== normalize(originalData.frontendOwnerId) ||
      normalize(formData.backendOwnerId) !== normalize(originalData.backendOwnerId) ||
      normalize(formData.testingOwnerId) !== normalize(originalData.testingOwnerId)
    console.log('hasChanges check:', { formData, originalData, changed })
    setHasChanges(changed)
  }, [formData, originalData])

  const handleSave = () => {
    updateMutation.mutate(
      { teamId, data: formData },
      {
        onSuccess: () => {
          setHasChanges(false)
        },
      }
    )
  }

  const handleReset = () => {
    if (originalData) {
      setFormData({ ...originalData })
    }
  }

  /**
   * 获取指定开发组的成员列表
   */
  const getMembersByGroups = (groups: DeveloperGroup[]) => {
    if (!members?.developers) {return []}
    const result: Array<{ id: string; employeeId: string; name: string }> = []
    for (const group of groups) {
      const groupMembers = members.developers[group] || []
      for (const member of groupMembers) {
        result.push({
          id: member.id,
          employeeId: member.employeeId,
          name: member.employee?.name || `员工 ${member.employeeId.slice(0, 8)}`,
        })
      }
    }
    return result
  }

  if (ownersLoading || membersLoading) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Settings className="h-4 w-4 animate-spin" />
          加载中...
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border bg-card">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-6 py-4">
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-medium">阶段负责人配置</h3>
        </div>
        {!isArchived && (
          <div className="flex items-center gap-2">
            {hasChanges && (
              <button
                onClick={handleReset}
                className="rounded-md px-3 py-1.5 text-sm font-medium hover:bg-muted"
              >
                重置
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={updateMutation.isPending || !hasChanges}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-medium",
                hasChanges
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-muted text-muted-foreground cursor-not-allowed",
                updateMutation.isPending && "opacity-50"
              )}
            >
              {updateMutation.isPending ? '保存中...' : '保存'}
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        <p className="mb-6 text-sm text-muted-foreground">
          配置团队各阶段的默认负责人，新任务创建时将自动使用这些配置。
        </p>

        <div className="space-y-6">
          {STAGE_OWNER_CONFIG.map((config) => {
            const availableMembers = getMembersByGroups(config.allowedGroups)
            const currentValue = formData[config.key]

            return (
              <div key={config.key} className="space-y-2">
                <label className="block text-sm font-medium">{config.label}</label>
                <p className="text-xs text-muted-foreground">{config.description}</p>
                <div className="flex items-center gap-3">
                  <Select
                    value={currentValue || 'none'}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        [config.key]: value === 'none' ? null : value,
                      })
                    }
                    disabled={isArchived}
                  >
                    <SelectTrigger className={cn(
                      'w-full max-w-md',
                      isArchived && 'cursor-not-allowed opacity-50'
                    )}>
                      <SelectValue placeholder="未设置" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">未设置</SelectItem>
                      {availableMembers.map((member) => (
                        <SelectItem key={member.employeeId} value={member.employeeId}>
                          {member.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {currentValue && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <User className="h-4 w-4" />
                      <span>已设置</span>
                    </div>
                  )}
                </div>
                {availableMembers.length === 0 && (
                  <p className="text-xs text-amber-600">
                    暂无可选成员，请先添加{config.allowedGroups.join('/')}组成员
                  </p>
                )}
              </div>
            )
          })}
        </div>

        {/* Error */}
        {updateMutation.error && (
          <div className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {updateMutation.error.message}
          </div>
        )}
      </div>
    </div>
  )
}
